/**
 * Accept/Reject card for ``suggest_bid_metadata_updates`` proposals, in Word.
 * When the same assistant message also has ``suggest_outline_changes``, the
 * outline card absorbs this one's section updates and this card stays out
 * of the way, as in the web app. Bid-level updates (title, ownership) are
 * not applied from the pane.
 */
import { type ToolCallMessagePartComponent, makeAssistantToolUI, useAuiState } from "@assistant-ui/react";
import { PencilLineIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { applyOutlineProposal } from "@/agents-v2/api/outlineProposal";
import { asRecord, getFirstStringValue, parseMaybeJson } from "@/agents-v2/utils/unknown";

import {
  BID_METADATA_PROPOSAL_TOOL_NAME,
  OUTLINE_PROPOSAL_TOOL_NAME,
  metadataUpdatesToOutlineChanges,
  parseBidMetadataProposal,
  parseOutlineProposal,
} from "./bid-proposal-merge";
import {
  type OutlineProposalDecision,
  readOutlineProposalDecision,
  writeOutlineProposalDecision,
} from "./outline-proposal-decisions";
import { useProposalEnv } from "./proposal-env";
import { ProposalReviewCard, ProposalReviewErrorCard } from "./proposal-review-card";

const BidMetadataProposalRender: ToolCallMessagePartComponent<unknown, unknown> = ({ result, status, toolCallId }) => {
  const { token, bidId: linkedBidId, notify, onApplied } = useProposalEnv();
  const messageParts = useAuiState((state) => state.message.parts);

  const absorbedByOutlineCard = useMemo(
    () =>
      messageParts.some(
        (part) =>
          part.type === "tool-call" &&
          part.toolName === OUTLINE_PROPOSAL_TOOL_NAME &&
          part.status?.type === "complete" &&
          parseOutlineProposal(part.result) !== null
      ),
    [messageParts]
  );

  const isComplete = status?.type === "complete";
  const proposal = useMemo(() => (isComplete ? parseBidMetadataProposal(result) : null), [isComplete, result]);
  const errorMessage = useMemo(() => {
    if (!isComplete || proposal) return null;
    const record = asRecord(parseMaybeJson(result));
    if (getFirstStringValue(record, ["status"]) === "error") {
      return getFirstStringValue(record, ["message", "error"]) || "Couldn't prepare the bid updates.";
    }
    return "Bid update couldn't be shown. Try asking again.";
  }, [isComplete, proposal, result]);

  const outlineChanges = useMemo(() => (proposal ? metadataUpdatesToOutlineChanges(proposal.updates) : []), [proposal]);
  const unsupported = useMemo(() => (proposal ? proposal.updates.filter((u) => u.target !== "section") : []), [proposal]);

  const bidId = proposal?.bidId || linkedBidId;
  const decisionKeys = useMemo(
    () => [...new Set([toolCallId, proposal?.proposalId].filter((k): k is string => Boolean(k)))],
    [proposal?.proposalId, toolCallId]
  );
  const loadDecision = useCallback((): OutlineProposalDecision | null => {
    for (const key of decisionKeys) {
      const stored = readOutlineProposalDecision(bidId, key);
      if (stored) return stored;
    }
    return null;
  }, [bidId, decisionKeys]);
  const [decision, setDecision] = useState<OutlineProposalDecision | null>(() => loadDecision());
  const [busy, setBusy] = useState(false);
  useEffect(() => setDecision(loadDecision()), [loadDecision]);
  const rememberDecision = useCallback(
    (next: OutlineProposalDecision) => {
      for (const key of decisionKeys) writeOutlineProposalDecision(bidId, key, next);
      setDecision(next);
    },
    [bidId, decisionKeys]
  );

  const onAccept = useCallback(async () => {
    if (!proposal || !token || decision || busy) return;
    if (!bidId) {
      notify("This proposal names no bid; link the document to a tender first.");
      return;
    }
    if (outlineChanges.length === 0) {
      notify(unsupported.length ? "Bid-level updates are applied in mytender, not from Word." : "No section updates to apply.");
      return;
    }
    setBusy(true);
    try {
      const response = await applyOutlineProposal(bidId, outlineChanges, token, proposal.summary);
      if (response.status !== "success") throw new Error(response.message || "Failed to apply bid updates");
      rememberDecision("accepted");
      notify(response.message || "Updates applied in mytender.");
      onApplied();
    } catch (error) {
      console.error(error);
      notify(error instanceof Error ? error.message : "Failed to apply bid updates");
    } finally {
      setBusy(false);
    }
  }, [bidId, busy, decision, notify, onApplied, outlineChanges, proposal, rememberDecision, token, unsupported.length]);

  const onReject = useCallback(() => {
    if (decision) return;
    rememberDecision("rejected");
  }, [decision, rememberDecision]);

  if (absorbedByOutlineCard) return null;
  if (!proposal) {
    if (!errorMessage) return null;
    return (
      <ProposalReviewErrorCard
        dataSlot="bid-metadata-proposal-error"
        icon={PencilLineIcon}
        title="Bid update unavailable"
        message={errorMessage}
      />
    );
  }
  const updateCount = proposal.updates.length;
  return (
    <ProposalReviewCard
      dataSlot="bid-metadata-proposal-result"
      icon={PencilLineIcon}
      title="Bid updates proposed"
      summary={<p>{proposal.summary}</p>}
      detail={
        <>
          {proposal.rationale ? <p>{proposal.rationale}</p> : null}
          <p>
            {updateCount} change{updateCount === 1 ? "" : "s"} pending
          </p>
        </>
      }
      decision={decision}
      acceptedLabel="Applied."
      busy={busy}
      onAccept={() => void onAccept()}
      onReject={onReject}
    />
  );
};

export const BidMetadataProposalToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: BID_METADATA_PROPOSAL_TOOL_NAME,
  render: BidMetadataProposalRender,
});
